// This component and all of its supporting ones grew out of an example provided on the MUI
// website for how to integrate react-window with MUI's Autocomplete component.
// See: https://mui.com/material-ui/react-autocomplete/#virtualization.
//
// It has some hacks to work around limitations in MUI's Autocomplete API, specifically,
// the fact that renderOption and renderGroup must return ReactElements. To work around
// this, we return a specially-typed object that masquerades as a ReactElement but
// actually contains the data we need to render the virtualized list. This is the exact
// approach show in the MUI example. In any case, it works!
//
// If the code is no longer available there, you can see the starting point the history of this file.

import { ReactElement, SyntheticEvent, useCallback, useRef, useState } from "react";
import { Popper, Autocomplete, TextField, Chip, Button } from "@mui/material";
import { autocompleteClasses } from "@mui/material/Autocomplete";
import { styled } from "@mui/material/styles";
import { useListRef } from "react-window";
import FlexRow from "../FlexRow";
import { AutocompleteOption } from "./types";
import { AutocompleteListbox } from "./AutocompleteListbox";

const StyledPopper = styled(Popper)({
  [`& .${autocompleteClasses.listbox}`]: {
    boxSizing: "border-box",
    "& ul": {
      padding: 0,
      margin: 0,
    },
  },
});

const ClearButton = (
  <Button
    variant="outlined"
    size="small"
    className="customClearBtn"
    sx={{ fontSize: "12px", padding: "0px", marginRight: "-4px" }}
  >
    Clear
  </Button>
);

// for extending style with `sx` prop
const styleX = {
  flexDirection: "column",
  width: "100%",
  "& .MuiInputBase-root": {
    paddingRight: "6px!important",
  },
  "& .MuiInputBase-input": {
    paddingRight: "35px!important",
  },
  "& .MuiFormLabel-root": {
    lineHeight: "unset",
  },
  "& .MuiAutocomplete-endAdornment": {
    // move the dropdown toggle and clear button next to the input
    position: "absolute",
    top: "unset",
    right: "0px",
    margin: "unset",
    padding: "unset",
    bottom: "0px",
    marginBottom: "-12px",
    "& .MuiIconButton-root:hover": {
      // the clearIcon content is wrapped in IconButton, but we don't want the hover effect
      border: "1px solid transparent!important",
      bgcolor: "transparent!important",
    },
  },
};

type Props = {
  options: AutocompleteOption[];
  selection: AutocompleteOption[];
  selectionHandler: (selection: AutocompleteOption[]) => void;
  label: string;
  placeholder?: string;
  helperText?: string;
  groupBy?: (option: AutocompleteOption) => string;
  chipIcon?: (
    options: AutocompleteOption
  ) => ReactElement<any, string | React.JSXElementConstructor<any>>;
};

export function VirtualizedAutocomplete({
  options,
  selection,
  label,
  placeholder,
  helperText,
  selectionHandler,
  groupBy,
  chipIcon,
}: Props) {
  const [inputValue, setInputValue] = useState("");

  // Use react-window v2's useListRef hook for imperative API access
  const internalListRef = useListRef(null);
  const optionIndexMapRef = useRef<Map<string, number>>(new Map());

  const handleItemsBuilt = useCallback((optionIndexMap: Map<string, number>) => {
    optionIndexMapRef.current = optionIndexMap;
  }, []);

  // Handle keyboard navigation by scrolling to highlighted option
  const handleHighlightChange = (event: SyntheticEvent, option: AutocompleteOption | null) => {
    if (option && internalListRef.current) {
      const index = optionIndexMapRef.current.get(option.key);
      if (index !== undefined) {
        internalListRef.current.scrollToRow({ index, align: "auto" });
      }
    }
  };

  return (
    <Autocomplete<AutocompleteOption, true>
      disablePortal
      multiple
      disableCloseOnSelect
      size="small"
      sx={styleX}
      value={selection}
      disableListWrap
      options={options}
      getOptionLabel={option => option.label}
      isOptionEqualToValue={(option, value) => option.key == value.key}
      inputValue={inputValue}
      clearIcon={ClearButton}
      onInputChange={(_, value, reason) => {
        if (reason != "reset") {
          // the search value was reset on each selection but
          // user may want to select >1 from filtered list
          setInputValue(value);
        }
      }}
      onChange={(_, selected) => {
        selectionHandler(selected);
      }}
      renderInput={params => (
        <FlexRow fullWidth>
          <TextField
            {...params}
            fullWidth
            size="small"
            label={label}
            placeholder={placeholder}
            helperText={helperText}
          />
        </FlexRow>
      )}
      renderOption={
        (props, option, state) =>
          ({
            props,
            label: option.label,
            labelSuffix: option.labelSuffix,
            selected: state.selected,
            index: state.index,
          }) as any // This is a VirtualAutocompleteOptionParams masquading as ReactElement
      }
      groupBy={groupBy}
      renderGroup={params => params as any}
      onHighlightChange={handleHighlightChange}
      renderTags={(value, getTagProps) => {
        /* Padding to avoid overlap of CLEAR ALL btn */
        return (
          <FlexRow fullWidth wrapItems autoScrollY style={{ maxHeight: 100, padding: 5 }}>
            {value.map((option, index) => (
              <Chip
                icon={chipIcon ? chipIcon(option) : undefined}
                size="small"
                label={option.label}
                {...getTagProps({ index })}
                key={index}
              />
            ))}
          </FlexRow>
        );
      }}
      slots={{ popper: StyledPopper }}
      slotProps={{
        listbox: {
          component: AutocompleteListbox,
          internalListRef,
          onItemsBuilt: handleItemsBuilt,
        } as any,
      }}
    />
  );
}
